import React, { useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link as RouterLink } from 'react-router-dom'

import { Check, KeyboardArrowDown } from '@mui/icons-material'
import { Box, Breadcrumbs, Divider, Link, ListItemIcon, ListItemText, Menu, MenuItem, Theme, Typography, lighten, useTheme } from '@mui/material'

import { GRTooltip } from '../../../../components/GRTooltip/GRTooltip'

export interface HierarchyTrailMenuOption {
  key: string
  label: string
  to: string
  state?: unknown
  // The option the trail is currently showing — marked, and inert.
  isCurrent?: boolean
  // Category color, matching the row color dot / `RankBadge` fill the ranked
  // list uses for the same id — lets a sibling menu visually agree with the
  // table it's switching within. Omitted options render without a dot rather
  // than a placeholder gray one.
  colorHex?: string
}

export interface HierarchyTrailMenu {
  // Accessible name of the menu list, e.g. "Switch event type".
  ariaLabel: string
  options: HierarchyTrailMenuOption[]
}

export interface HierarchyTrailSegment {
  key: string
  label: string
  // Router target — renders the segment as a link.
  to?: string
  // Router navigation state carried along with `to`.
  state?: unknown
  // Alternative to `to` for in-place navigation (renders a button-styled link).
  onClick?: () => void
  // Siblings at this level. Turns the segment into a dropdown trigger so the
  // user can switch sideways without going back up the hierarchy. Ignored when
  // every option is the current one (nothing to move to). Combined with `to` the
  // crumb splits: label navigates, chevron opens the sibling menu.
  menu?: HierarchyTrailMenu
  // Level that has no value yet: grayed out and inert, so the trail still shows
  // how deep the hierarchy goes before anything is selected.
  isPlaceholder?: boolean
  // Hover copy for the crumb. Mainly for placeholders, where the label alone
  // can't say *why* the level is empty.
  tooltip?: string
  // Level currently being viewed — emphasized as the end of the trail.
  isCurrent?: boolean
  // Small caption above the label naming the level ("Event type"). When any
  // segment carries one, give the container a taller height (see
  // `HIERARCHY_TRAIL_LABELED_HEIGHT`) so the two lines fit inside the plate.
  levelLabel?: string
  // Open `to` in a new tab (`rel="noopener"`). For trails inside a dialog, where
  // navigating away would close what the user is looking at.
  openInNewTab?: boolean
  // Small adornment after the label (e.g. a discontinued marker).
  icon?: React.ReactNode
}

// Container heights the trail is designed for: the analytics hierarchy bar
// (labels only) and the labeled variant used under the event dialog title.
export const HIERARCHY_TRAIL_HEIGHT = 48
export const HIERARCHY_TRAIL_LABELED_HEIGHT = 56

// Width of the chevron separator between plates. Matches the SVG viewBox width.
const SEPARATOR_WIDTH = 20

// Anchor attributes for a crumb that leaves the current page.
const newTabProps = (segment: HierarchyTrailSegment) => (segment.openInNewTab ? { target: '_blank', rel: 'noopener' } : {})

// Keeps a crumb's color/weight identical whether it renders as plain text or as
// a dropdown trigger, so adding siblings never reshuffles the trail's hierarchy.
const segmentTextColor = (segment: HierarchyTrailSegment): string =>
  segment.isPlaceholder ? 'text.disabled' : segment.isCurrent ? 'primary.contrastText' : 'text.secondary'

// Text color for a crumb that is a link: dark primary on the light plates, white
// on the solid primary plate of the level being viewed (a crumb can be both
// current and a link, e.g. a category page whose crumb navigates to itself).
const linkTextColor = (segment: HierarchyTrailSegment): string => (segment.isCurrent ? 'primary.contrastText' : 'primary.dark')

// Makes a crumb's link / button cover its entire plate, not just the label text.
// The pseudo-element is positioned against the plate (`li`, `position: relative`),
// so the whole step is the click target while the markup stays one real link.
const WHOLE_PLATE_HIT_AREA_SX = {
  // MUI's `Link component="button"` is `position: relative` by default, which
  // would make the button itself the containing block and shrink the hit area
  // back to the label.
  position: 'static',
  '&::after': { content: '""', position: 'absolute', inset: 0 },
}

// A plate fill. Must be opaque: the chevron separator paints one plate's color
// over the next plate's background, so a translucent fill would compound there.
type PlateColor = string | ((theme: Theme) => string)

// Plates tell the user where they are: the level being viewed is solid primary,
// the levels above it (already chosen, and clickable) a light primary tint, and
// levels not reached yet stay neutral gray.
const plateBackground = (segment: HierarchyTrailSegment): PlateColor => {
  if (segment.isCurrent) return 'primary.main'
  if (segment.isPlaceholder) return 'grey.100'
  return (theme) => lighten(theme.palette.primary.main, 0.88)
}

// Segmented-breadcrumb separator: a full-height chevron whose triangular fill
// (`currentColor`, set per separator below) carries the previous segment's
// background over the next segment's, so each segment reads as a chevron-shaped
// plate rather than a rectangle. The open edge of the triangle is stroked with
// the theme divider color as the 1px boundary line between segments.
const FullHeightChevron: React.FC = () => {
  const theme = useTheme()

  return (
    <Box aria-hidden component="svg" viewBox="0 0 20 40" preserveAspectRatio="none" sx={{ height: '100%', width: SEPARATOR_WIDTH, display: 'block' }}>
      <path d="M 0 0 L 20 20 L 0 40 Z" fill="currentColor" />
      <path
        d="M 0 0 L 20 20 L 0 40"
        fill="none"
        stroke={theme.palette.divider}
        strokeWidth={1}
        strokeLinecap="butt"
        strokeLinejoin="miter"
        vectorEffect="non-scaling-stroke"
      />
    </Box>
  )
}

// `Breadcrumbs` alternates crumb / separator `<li>`s, so the crumb at `index`
// is the `2 * index + 1`-th list item and the separator in front of it the
// `2 * index`-th. Building the tinting from the actual segments (rather
// than hardcoded nth-of-type rules) keeps partial trails — 2 crumbs on the
// catalogue, 4 on a blueprint page — correctly shaded.
function buildSegmentTintSx(segments: HierarchyTrailSegment[]): Record<string, object> {
  const sx: Record<string, object> = {}
  const count = segments.length
  for (let index = 0; index < count; index += 1) {
    const background = plateBackground(segments[index])
    sx[`& li:nth-of-type(${2 * index + 1})`] = {
      bgcolor: background,
      // Every plate starts at its label's width and shares the leftover space
      // equally, so the steps widen across the whole bar and longer labels get
      // proportionally more room. Labels ellipsize when the bar is too narrow.
      flex: '1 1 auto',
    }
    if (index > 0) {
      // The separator sits on this segment's plate while its chevron fill
      // carries the previous segment's color across the seam.
      sx[`& li:nth-of-type(${2 * index})`] = { bgcolor: background, color: plateBackground(segments[index - 1]) }
    }
  }
  return sx
}

// Category color dot, matching the 10px dot `CatalogueRowHoverCard` shows next
// to its category trail and the fill `RankBadge` uses for the same id — so a
// sibling menu option agrees with the row it switches to.
const MenuOptionColorDot: React.FC<{ colorHex?: string }> = ({ colorHex }) =>
  colorHex ? (
    <Box
      aria-hidden="true"
      data-testid="hierarchy-option-color-dot"
      sx={{ width: 10, height: 10, borderRadius: '50%', backgroundColor: colorHex, flexShrink: 0 }}
    />
  ) : null

// A crumb whose siblings are switchable. The whole plate is the menu trigger, so
// every step with siblings behaves the same way: click anywhere to switch level.
// A crumb that also has its own page (`to`) gets an "Open <name>" first item in
// the menu, so that page stays reachable without a second click target on the plate.
const HierarchyTrailDropdown: React.FC<{ segment: HierarchyTrailSegment; menu: HierarchyTrailMenu }> = ({ segment, menu }) => {
  const { t } = useTranslation()
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)
  const close = useCallback(() => setAnchorEl(null), [])
  const open = useCallback((event: React.MouseEvent<HTMLElement>) => setAnchorEl(event.currentTarget), [])

  return (
    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.25, minWidth: 0 }}>
      <Link
        component="button"
        type="button"
        onClick={open}
        underline="hover"
        variant="body1"
        aria-haspopup="menu"
        aria-expanded={Boolean(anchorEl)}
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 0.25,
          minWidth: 0,
          border: 0,
          background: 'none',
          p: 0,
          cursor: 'pointer',
          ...WHOLE_PLATE_HIT_AREA_SX,
          // An unresolved level is still pickable here, so it reads as muted
          // rather than disabled — unlike a placeholder with nothing to offer.
          color: segment.isPlaceholder ? 'text.secondary' : segmentTextColor(segment),
          fontWeight: segment.isCurrent ? 700 : 'normal',
          // The current plate is solid primary, so a primary hover color would vanish into it.
          '&:hover': segment.isCurrent ? { opacity: 0.85 } : { color: 'primary.main' },
        }}
      >
        <Box component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {segment.label}
        </Box>
        <KeyboardArrowDown sx={{ fontSize: 18, flexShrink: 0 }} />
      </Link>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={close}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        MenuListProps={{ 'aria-label': menu.ariaLabel, dense: true }}
        // Long levels (a category can hold dozens of blueprints) scroll inside
        // the menu instead of running off the viewport.
        PaperProps={{ sx: { maxHeight: 380, maxWidth: 360 } }}
      >
        {segment.to ? (
          <MenuItem component={RouterLink} to={segment.to} state={segment.state} onClick={close} {...newTabProps(segment)}>
            <ListItemText primaryTypographyProps={{ variant: 'body2', fontWeight: 700, noWrap: true, color: 'primary' }}>
              {t('market-analytics:hierarchy_menu_open_level', { name: segment.label })}
            </ListItemText>
          </MenuItem>
        ) : null}
        {segment.to ? <Divider /> : null}
        {menu.options.map((option) =>
          option.isCurrent ? (
            // `disabled` is what makes this option inert (no target to navigate
            // to), not a statement that it's unavailable — it should read as the
            // selected one, not a grayed-out one. MUI's own disabled rule
            // (`.MuiMenuItem-root.Mui-disabled { opacity: … }`) is a two-class
            // selector, so a bare `opacity: 1` here loses on specificity no
            // matter the insertion order; it has to be undone through the same
            // compound selector.
            <MenuItem key={option.key} disabled sx={{ '&.Mui-disabled': { opacity: 1 } }}>
              {/* Same left slot every option gets (the color dot below), so the
                  label's left edge lines up whether or not this is the current
                  row. */}
              <ListItemIcon>
                <MenuOptionColorDot colorHex={option.colorHex} />
              </ListItemIcon>
              <ListItemText primaryTypographyProps={{ variant: 'body2', fontWeight: 700, noWrap: true, color: 'primary' }}>{option.label}</ListItemText>
              <Check fontSize="small" color="primary" sx={{ ml: 1.5, flexShrink: 0 }} />
            </MenuItem>
          ) : (
            <MenuItem key={option.key} component={RouterLink} to={option.to} state={option.state} onClick={close}>
              <ListItemIcon>
                <MenuOptionColorDot colorHex={option.colorHex} />
              </ListItemIcon>
              <ListItemText primaryTypographyProps={{ variant: 'body2', noWrap: true }}>{option.label}</ListItemText>
            </MenuItem>
          )
        )}
      </Menu>
    </Box>
  )
}

// The crumb's value line, in the flavor its segment asks for.
function renderCrumbValue(segment: HierarchyTrailSegment): React.ReactElement {
  // An option that is already being shown is not a destination, so a menu of
  // nothing but the current level is just the crumb itself.
  if (segment.menu?.options.some((option) => !option.isCurrent)) {
    return <HierarchyTrailDropdown segment={segment} menu={segment.menu} />
  }

  if (segment.to) {
    return (
      <Link
        component={RouterLink}
        to={segment.to}
        state={segment.state}
        {...newTabProps(segment)}
        underline="hover"
        color={linkTextColor(segment)}
        variant="body1"
        sx={{ fontWeight: 700, ...WHOLE_PLATE_HIT_AREA_SX }}
      >
        {segment.label}
      </Link>
    )
  }

  if (segment.onClick) {
    return (
      <Link
        component="button"
        type="button"
        onClick={segment.onClick}
        underline="hover"
        color={linkTextColor(segment)}
        variant="body1"
        sx={{ fontWeight: 700, border: 0, background: 'none', p: 0, cursor: 'pointer', font: 'inherit', ...WHOLE_PLATE_HIT_AREA_SX }}
      >
        {segment.label}
      </Link>
    )
  }

  return (
    <Typography variant="body1" color={segmentTextColor(segment)} sx={{ fontWeight: segment.isCurrent ? 700 : 'normal' }}>
      {segment.label}
    </Typography>
  )
}

// One crumb: the value line, plus the level caption above and the adornment
// beside it when the segment carries them. Split out from the trail so a
// segment `tooltip` can wrap any flavor without repeating them.
function renderCrumb(segment: HierarchyTrailSegment): React.ReactElement {
  const value = renderCrumbValue(segment)
  const withIcon = segment.icon ? (
    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, minWidth: 0 }}>
      <Box sx={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis' }}>{value}</Box>
      {segment.icon}
    </Box>
  ) : (
    value
  )

  if (!segment.levelLabel) return React.cloneElement(withIcon, { key: segment.key })

  return (
    <Box key={segment.key} sx={{ display: 'flex', flexDirection: 'column', minWidth: 0, lineHeight: 1.2 }}>
      <Typography
        component="span"
        variant="caption"
        color={segment.isPlaceholder ? 'text.disabled' : segment.isCurrent ? 'primary.contrastText' : 'text.secondary'}
        sx={{ textTransform: 'uppercase', letterSpacing: '0.04em', fontSize: '0.68rem', lineHeight: 1.2 }}
      >
        {segment.levelLabel}
      </Typography>
      <Box sx={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis' }}>{withIcon}</Box>
    </Box>
  )
}

interface BlueprintHierarchyTrailProps {
  segments: HierarchyTrailSegment[]
  ariaLabel: string
}

/**
 * Presentational `Event Category › Event Type › Blueprint` trail: chevron-shaped
 * segment plates with per-segment link / click / dropdown / placeholder
 * rendering. Carries no sticky or `Card` wrapper of its own so it can serve the
 * page-level hierarchy bar as well as inline placements (event dialog title
 * block).
 *
 * Stretches to its container's height — give the container a fixed height so
 * the chevrons cut across the whole trail edge to edge.
 */
export const BlueprintHierarchyTrail: React.FC<BlueprintHierarchyTrailProps> = ({ segments, ariaLabel }) => {
  const tintSx = useMemo(() => buildSegmentTintSx(segments), [segments])

  return (
    <Breadcrumbs
      separator={<FullHeightChevron />}
      aria-label={ariaLabel}
      sx={{
        minWidth: 0,
        width: '100%',
        height: '100%',
        // The <ol> fills the bar so its list items can stretch to full height
        // and the last segment can grow to the right edge.
        '& .MuiBreadcrumbs-ol': { width: '100%', height: '100%', flexWrap: 'nowrap' },
        // Every list item spans the full height: text crumbs center their label
        // vertically, separator items let the chevron SVG stretch
        // floor-to-ceiling.
        '& .MuiBreadcrumbs-li': { display: 'flex', alignItems: 'center', height: '100%', px: 2.5, minWidth: 0, position: 'relative' },
        '& .MuiBreadcrumbs-separator': { height: '100%', width: SEPARATOR_WIDTH, flex: `0 0 ${SEPARATOR_WIDTH}px`, mx: 0 },
        '& .MuiBreadcrumbs-li > *': { whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' },
        ...tintSx,
      }}
    >
      {segments.map((segment) => {
        const crumb = renderCrumb(segment)

        return segment.tooltip ? (
          <GRTooltip
            key={segment.key}
            content={segment.tooltip}
            styles={{ minWidth: 0, display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', cursor: 'help' }}
          >
            {crumb}
          </GRTooltip>
        ) : (
          crumb
        )
      })}
    </Breadcrumbs>
  )
}
